/*
 * Every colour, space, radius, font and shadow here is a design token.
 * The only literals are the arrow's geometry, which is structural.
 */

.tip,
.tip * {
  box-sizing: border-box;
}

/* Wraps the trigger without changing how it sits in a line. Not display: contents —
   there'd be no box to measure, and the whole thing is positioned by measurement. */
.trigger {
  display: inline-flex;
}

.tip {
  /* The UA sheet centres a popover with inset: 0 + margin: auto. Both are cleared so the
     measured top/left land where they're put. Same as Menu. */
  inset: auto;
  margin: 0;
  padding: var(--space-2) var(--space-3);

  /* Also the UA sheet's: [popover] gets overflow: auto, which clips the arrow at the
     edge it hangs over. Menu never hit this because nothing of its sticks out. */
  overflow: visible;

  max-width: 320px;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--bg-elevated);
  box-shadow: var(--shadow-lg);

  font-family: var(--font-sans);

  /* Pointers pass straight through. A tooltip that sits under the cursor would take the
     pointerleave the trigger is waiting for, and flicker as it fought itself. */
  pointer-events: none;

  opacity: 0;
  transition:
    opacity var(--duration-fast) var(--ease-default),
    translate var(--duration-fast) var(--ease-default),
    overlay var(--duration-fast) allow-discrete,
    display var(--duration-fast) allow-discrete;
}

/* Slides in from the side it's on, so the motion reads as coming out of the trigger. */
.tip[data-side='top'] {
  translate: 0 var(--space-1);
}
.tip[data-side='bottom'] {
  translate: 0 calc(var(--space-1) * -1);
}
.tip[data-side='left'] {
  translate: var(--space-1) 0;
}
.tip[data-side='right'] {
  translate: calc(var(--space-1) * -1) 0;
}

.tip:popover-open {
  opacity: 1;
  translate: 0 0;
}

@starting-style {
  .tip:popover-open {
    opacity: 0;
  }
}

/* ── Arrow ── */

/*
 * A square rotated 45°, with border on two adjacent sides so only the outer corner shows
 * a hairline. The reference drew twelve clip-path polygons for this — one per side and
 * alignment — and pinned each to a percentage of the tooltip's own width, so the point
 * drifted off the trigger whenever the box was wide or clamped to an edge.
 *
 * --anchor-centre is the trigger's midpoint measured inside this box (see internal/
 * anchor.ts), so the arrow stays on the trigger even when the box has been pulled back
 * from the viewport edge. Clamped so it can't ride out over the rounded corners.
 */
.arrow {
  --arrow-size: 8px;
  --arrow-inset: calc(var(--arrow-size) / -2 - 1px);
  --arrow-limit: calc(var(--radius-md) + var(--arrow-size));

  position: absolute;
  width: var(--arrow-size);
  height: var(--arrow-size);

  background: var(--bg-elevated);
  border: 1px solid var(--border-default);
}

.tip[data-side='top'] .arrow,
.tip[data-side='bottom'] .arrow {
  left: clamp(var(--arrow-limit), var(--anchor-centre, 50%), calc(100% - var(--arrow-limit)));
}

.tip[data-side='left'] .arrow,
.tip[data-side='right'] .arrow {
  top: clamp(var(--arrow-limit), var(--anchor-centre, 50%), calc(100% - var(--arrow-limit)));
}

.tip[data-side='top'] .arrow {
  bottom: var(--arrow-inset);
  translate: -50% 0;
  rotate: 45deg;
  border-top: 0;
  border-left: 0;
}

.tip[data-side='bottom'] .arrow {
  top: var(--arrow-inset);
  translate: -50% 0;
  rotate: 45deg;
  border-right: 0;
  border-bottom: 0;
}

.tip[data-side='left'] .arrow {
  right: var(--arrow-inset);
  translate: 0 -50%;
  rotate: 45deg;
  border-bottom: 0;
  border-left: 0;
}

.tip[data-side='right'] .arrow {
  left: var(--arrow-inset);
  translate: 0 -50%;
  rotate: 45deg;
  border-top: 0;
  border-right: 0;
}

/* ── Content ── */

.title {
  margin: 0 0 var(--space-1);
  font-size: var(--text-xs);
  font-weight: var(--weight-semi);
  line-height: var(--leading-normal);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wide);
  color: var(--text-muted);
}

.content {
  margin: 0;
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
  color: var(--text-primary);
}

@media (prefers-reduced-motion: reduce) {
  .tip {
    transition-property: overlay, display;
  }
}
